import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Camera,
  Trash2,
  Save,
  Pencil
} from 'lucide-react';
import { Client, CostCenter } from '../types';
import type {
  ReworkIncident,
  IncidentType,
  IncidentOrigin,
  IncidentStatus,
  IncidentPriority,
  ResponsibleDept
} from './ReworkAndReturnsManager';

export const PHOTO_SLOT_LABELS = [
  'Foto 1: Vista General / Etiqueta',
  'Foto 2: Detalle Defecto / Zona Afectada',
  'Foto 3: Macro / Acercamiento',
  'Foto 4: Ángulo Lateral / Perfil',
  'Foto 5: Lote / Marcaje de Pieza',
  'Foto 6: Reinspección / Proceso',
  'Foto 7: Evidencia Adicional'
];

export interface EditFormData {
  id: string;
  folioId: string;
  clientId: string;
  clientName: string;
  costCenterId: string;
  costCenterName: string;
  type: IncidentType;
  originStage: IncidentOrigin;
  affectedPieces: number;
  reworkPiecesName: string;
  causeCategory: string;
  causeDetail: string;
  actionPlan: string;
  resolutionNotes: string;
  status: IncidentStatus;
  priority: IncidentPriority;
  responsibleDept: ResponsibleDept;
  costEstimateMXN: number;
  reportedBy: string;
  dateReported: string;
  photos: string[];
}

interface EditReworkIncidentModalProps {
  isOpen: boolean;
  editFormData: EditFormData;
  setEditFormData: React.Dispatch<React.SetStateAction<EditFormData>>;
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  clients: Client[];
  costCenters: CostCenter[];
  handleEditPhotoUpload: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEditRemovePhoto: (index: number) => void;
  editFileInputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
}

export const EditReworkIncidentModal: React.FC<EditReworkIncidentModalProps> = ({
  isOpen,
  editFormData,
  setEditFormData,
  onClose,
  onSave,
  clients,
  costCenters,
  handleEditPhotoUpload,
  handleEditRemovePhoto,
  editFileInputRefs
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 z-[999999]">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#141514] border border-amber-500/50 rounded-3xl max-w-4xl w-full p-6 space-y-6 shadow-2xl max-h-[90vh] flex flex-col overflow-hidden text-slate-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#2a2d2a] pb-4 shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-500/20 text-amber-400 rounded-2xl border border-amber-500/30">
                <Pencil className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-black text-white flex items-center gap-2">
                  <span>Editar Proceso Completo de Incidencia</span>
                  <span className="text-xs bg-amber-500/20 text-amber-300 px-2.5 py-0.5 rounded-full border border-amber-500/40 font-mono font-bold">
                    {editFormData.id}
                  </span>
                </h2>
                <p className="text-xs text-slate-400">
                  Modifique folio, fecha, número de piezas, causas, plan de acción y actualice o agregue fotos de evidencia (hasta 7 fotos).
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-white p-2 rounded-xl hover:bg-[#222422] transition-colors cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form Body */}
          <form id="edit-incident-form-modal" onSubmit={onSave} className="space-y-5 overflow-y-auto pr-2 custom-scrollbar flex-1">
            
            {/* Row 1: Folio, Fecha, Tipo, Origen */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-[#1c1d1c] p-4 rounded-2xl border border-[#2d302d]">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Folio Asociado</label>
                <input
                  type="text"
                  required
                  value={editFormData.folioId}
                  onChange={e => setEditFormData(prev => ({ ...prev, folioId: e.target.value }))}
                  placeholder="F-XXXXXX"
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs font-mono font-bold text-white focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Fecha del Reporte</label>
                <input
                  type="date"
                  required
                  value={editFormData.dateReported}
                  onChange={e => setEditFormData(prev => ({ ...prev, dateReported: e.target.value }))}
                  style={{ colorScheme: 'dark' }}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-amber-500 cursor-pointer"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Tipo de Incidencia</label>
                <select
                  value={editFormData.type}
                  onChange={e => setEditFormData(prev => ({ ...prev, type: e.target.value as IncidentType }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="RETRABAJO">🔄 Retrabajo (Reproceso)</option>
                  <option value="DEVOLUCION">🔄 Retrabajo Cliente</option>
                  <option value="SCRAP">🗑️ Scrap (Item Dañado)</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Fase de Origen</label>
                <select
                  value={editFormData.originStage}
                  onChange={e => setEditFormData(prev => ({ ...prev, originStage: e.target.value as IncidentOrigin }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="INSPECCION">🔍 Fase de Inspección (CC)</option>
                  <option value="ENTREGA_PLANTA">🚚 Fase Entrega a Planta</option>
                  <option value="SCRAP_ITEM_DANADO">⚠️ SCRAP/ITEM DAÑADO</option>
                </select>
              </div>
            </div>

            {/* Row 2: Cliente & Centro de Costos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#1c1d1c] p-4 rounded-2xl border border-[#2d302d]">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Cliente</label>
                <select
                  value={editFormData.clientId}
                  onChange={e => {
                    const cl = clients.find(c => c.id === e.target.value);
                    setEditFormData(prev => ({
                      ...prev,
                      clientId: e.target.value,
                      clientName: cl?.name || prev.clientName
                    }));
                  }}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Centro de Costos</label>
                <select
                  value={editFormData.costCenterId}
                  onChange={e => {
                    const cc = costCenters.find(c => c.id === e.target.value);
                    setEditFormData(prev => ({
                      ...prev,
                      costCenterId: e.target.value,
                      costCenterName: cc?.name || prev.costCenterName
                    }));
                  }}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  {costCenters.map(cc => (
                    <option key={cc.id} value={cc.id}>{cc.name} ({cc.code})</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: Piezas Afectadas & Nombre / Descripción */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-[#1c1d1c] p-4 rounded-2xl border border-[#2d302d]">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Número de Piezas Afectadas</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={editFormData.affectedPieces}
                  onChange={e => setEditFormData(prev => ({ ...prev, affectedPieces: Number(e.target.value) || 1 }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs font-mono font-bold text-amber-300 focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Descripción / Piezas a Retrabajar</label>
                <input
                  type="text"
                  value={editFormData.reworkPiecesName}
                  onChange={e => setEditFormData(prev => ({ ...prev, reworkPiecesName: e.target.value }))}
                  placeholder="Ej. Soportes de suspensión trasera con falta de espesor"
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>

            {/* Row 4: Causa y Detalle */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#1c1d1c] p-4 rounded-2xl border border-[#2d302d]">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Categoría del Defecto</label>
                <select
                  value={editFormData.causeCategory}
                  onChange={e => setEditFormData(prev => ({ ...prev, causeCategory: e.target.value }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="MATERIAL DAÑADO">⚠️ MATERIAL DAÑADO</option>
                  <option value="Mala Adherencia de Pintura">Mala Adherencia de Pintura</option>
                  <option value="Decapado Incompleto">Decapado Incompleto</option>
                  <option value="Oxidación / Humedad">Oxidación / Humedad</option>
                  <option value="Golpe en Transporte">Golpe en Transporte</option>
                  <option value="Especificación Incorrecta">Especificación Incorrecta</option>
                  <option value="Rechazo Cliente en Planta">Rechazo Cliente en Planta</option>
                  <option value="Grumos / Piel de Naranja">Grumos / Piel de Naranja</option>
                  <option value="Contaminación por Aceite">Contaminación por Aceite</option>
                  <option value="Otro Defecto">Otro Defecto</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Reportado por</label>
                <input
                  type="text"
                  value={editFormData.reportedBy}
                  onChange={e => setEditFormData(prev => ({ ...prev, reportedBy: e.target.value }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                />
              </div>

              <div className="space-y-1 sm:col-span-2">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Detalle del Hallazgo / Observaciones del Defecto</label>
                <textarea
                  rows={2}
                  value={editFormData.causeDetail}
                  onChange={e => setEditFormData(prev => ({ ...prev, causeDetail: e.target.value }))}
                  placeholder="Describa a detalle qué se encontró, zonas afectadas y condiciones del material..."
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl p-3 text-xs text-white focus:outline-none focus:border-amber-500 resize-none"
                />
              </div>
            </div>

            {/* Row 5: Plan de Acción e Instrucciones Técnicas & Notas de Resolución */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-[#1c1d1c] p-4 rounded-2xl border border-[#2d302d]">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-emerald-400 block font-mono">Plan de Acción / Instrucciones Técnicas</label>
                <textarea
                  rows={3}
                  value={editFormData.actionPlan}
                  onChange={e => setEditFormData(prev => ({ ...prev, actionPlan: e.target.value }))}
                  placeholder="Instrucciones precisas para el operario (ej. Realizar decapado químico 20 min, aplicar granalla fina y recubrir con imprimación epoxy)..."
                  className="w-full bg-[#141514] border border-emerald-500/40 rounded-xl p-3 text-xs text-emerald-300 focus:outline-none focus:border-emerald-400 resize-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-cyan-400 block font-mono">Notas de Resolución / Cierre</label>
                <textarea
                  rows={3}
                  value={editFormData.resolutionNotes}
                  onChange={e => setEditFormData(prev => ({ ...prev, resolutionNotes: e.target.value }))}
                  placeholder="Notas de reinspección, aprobación de calidad o comentarios de entrega..."
                  className="w-full bg-[#141514] border border-cyan-500/40 rounded-xl p-3 text-xs text-cyan-300 focus:outline-none focus:border-cyan-400 resize-none"
                />
              </div>
            </div>

            {/* Row 6: Estatus, Prioridad, Responsable, Costo */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-[#1c1d1c] p-4 rounded-2xl border border-[#2d302d]">
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Estatus del Proceso</label>
                <select
                  value={editFormData.status}
                  onChange={e => setEditFormData(prev => ({ ...prev, status: e.target.value as IncidentStatus }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs font-bold text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="PENDIENTE">⏳ PENDIENTE</option>
                  <option value="EN_REPROCESO">🔄 EN REPROCESO</option>
                  <option value="RESUELTO">✅ RESUELTO</option>
                  <option value="REINSPECCIONADO">🔍 REINSPECCIONADO</option>
                  <option value="DESECHADO">🗑️ DESECHADO</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Prioridad</label>
                <select
                  value={editFormData.priority}
                  onChange={e => setEditFormData(prev => ({ ...prev, priority: e.target.value as IncidentPriority }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="BAJA">Baja</option>
                  <option value="MEDIA">Media</option>
                  <option value="ALTA">Alta</option>
                  <option value="CRITICA">Crítica</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Dept. Responsable</label>
                <select
                  value={editFormData.responsibleDept}
                  onChange={e => setEditFormData(prev => ({ ...prev, responsibleDept: e.target.value as ResponsibleDept }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="CALIDAD">Calidad</option>
                  <option value="DECAPADO_PINTURA">Decapado y Pintura</option>
                  <option value="LOGISTICA_TRANSPORTE">Logística y Transporte</option>
                  <option value="PRODUCCION">Producción</option>
                  <option value="CLIENTE">Cliente</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold text-slate-300 block font-mono">Costo Estimado (MXN)</label>
                <input
                  type="number"
                  min="0"
                  value={editFormData.costEstimateMXN}
                  onChange={e => setEditFormData(prev => ({ ...prev, costEstimateMXN: Number(e.target.value) || 0 }))}
                  className="w-full bg-[#141514] border border-[#3a3d3a] rounded-xl px-3 py-2 text-xs font-mono text-emerald-400 focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>

            {/* Row 7: Evidencia Fotográfica (Hasta 7 Fotos) */}
            <div className="space-y-3 bg-[#1c1d1c] p-4 rounded-2xl border border-[#2d302d]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold text-white flex items-center gap-2 font-mono uppercase tracking-wider">
                    <Camera className="h-4 w-4 text-cyan-400" />
                    <span>Evidencia Fotográfica (Hasta 7 Fotografías)</span>
                  </h3>
                  <p className="text-[10px] text-slate-400">
                    Cargue o reemplace fotos de evidencia del defecto y del estado de las piezas.
                  </p>
                </div>
                <span className="text-[10.5px] font-mono text-cyan-400 font-bold bg-cyan-950/50 border border-cyan-800/60 px-2.5 py-0.5 rounded-full">
                  {editFormData.photos.filter(p => Boolean(p)).length} / 7 anexadas
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
                {PHOTO_SLOT_LABELS.map((slotLabel, index) => {
                  const photoUrl = editFormData.photos[index];
                  return (
                    <div
                      key={index}
                      className="bg-[#141514] border border-[#333633] rounded-xl p-2 flex flex-col items-center justify-between text-center relative group min-h-[140px]"
                    >
                      <span className="text-[9px] font-mono font-bold text-slate-400 truncate w-full mb-1" title={slotLabel}>
                        Foto #{index + 1}
                      </span>

                      {photoUrl ? (
                        <div className="relative w-full h-20 rounded-lg overflow-hidden border border-[#444] bg-black">
                          <img
                            src={photoUrl}
                            alt={`Foto ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => handleEditRemovePhoto(index)}
                            title="Eliminar foto"
                            className="absolute top-1 right-1 bg-red-600/90 hover:bg-red-600 text-white p-1 rounded-md transition-all shadow-md cursor-pointer"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => editFileInputRefs.current[index]?.click()}
                          className="w-full h-20 rounded-lg border-2 border-dashed border-[#3a3d3a] hover:border-amber-400/60 bg-[#111211] flex flex-col items-center justify-center text-slate-500 hover:text-amber-300 transition-all cursor-pointer p-1"
                        >
                          <Camera className="h-5 w-5 mb-1 opacity-70" />
                          <span className="text-[8.5px] font-bold leading-tight">Agregar Foto</span>
                        </div>
                      )}

                      <div className="mt-2 w-full">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={el => (editFileInputRefs.current[index] = el)}
                          onChange={e => handleEditPhotoUpload(index, e)}
                        />
                        <button
                          type="button"
                          onClick={() => editFileInputRefs.current[index]?.click()}
                          className="w-full py-1 bg-[#222422] hover:bg-[#2e312e] text-[9.5px] font-bold text-slate-300 hover:text-white rounded-lg border border-[#3a3d3a] transition-colors cursor-pointer"
                        >
                          {photoUrl ? 'Cambiar' : 'Subir'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </form>

          {/* Modal Footer */}
          <div className="flex items-center justify-between border-t border-[#2a2d2a] pt-4 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-[#222422] hover:bg-[#2c2f2c] text-slate-300 text-xs font-bold rounded-xl transition-all cursor-pointer"
            >
              Cancelar
            </button>

            <button
              type="submit"
              form="edit-incident-form-modal"
              className="px-5 py-2 bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-xs font-black rounded-xl shadow-lg shadow-amber-950/50 border border-amber-400/40 flex items-center gap-2 transition-all cursor-pointer hover:scale-105 active:scale-95"
            >
              <Save className="h-4 w-4" />
              <span>Guardar Modificaciones del Proceso</span>
            </button>
          </div>

        </motion.div>
      </div>
    </AnimatePresence>
  );
};
